import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { BookOpen, Gauge, LineChart, Lock, Shield, CheckCircle2 } from "lucide-react";
import PageShell from "@/components/brand/PageShell";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import CandlestickBackground from "@/components/hero/CandlestickBackground";
import Testimonials from "@/components/testimonials/Testimonials";

const TRUST_STRIP_ITEMS = [
    "CURRICULUM-FIRST",
    "NO SIGNAL SERVICE",
    "NO DAILY ALERTS",
    "TRANSPARENT 12-MONTH STRUCTURE",
    "RISK DISCLOSED",
];

const PHASES = [
    {
        n: "01", name: "Education", duration: "Months 1–9", locked: false,
        bullets: ["Market mechanics", "Technical analysis frameworks", "Risk management", "Trade thesis construction", "Psychology of patience"],
    },
    {
        n: "02", name: "Paper Trading", duration: "Month 10", locked: true,
        bullets: ["Apply the curriculum in a risk-free simulation", "Journal every trade", "Refine your thesis discipline"],
    },
    {
        n: "03", name: "Live Sessions", duration: "Months 11–12", locked: true,
        bullets: ["Weekly live trading sessions with the instructor", "Real market conditions", "Real accountability"],
    },
];

function CheckoutButtons({ variant = "primary", plan, children, className = "", testId }) {
    const { user, status } = useAuth();
    const navigate = useNavigate();
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);

    async function handleClick() {
        if (status !== "yes" || !user) {
            navigate(`/signup?intent=${plan}`);
            return;
        }
        if (user.role !== "student") {
            setError("Enrollment is limited to student accounts.");
            return;
        }
        setBusy(true);
        setError(null);
        try {
            const res = await api.billingCheckout(plan, window.location.origin);
            window.location.href = res.url;
        } catch (err) {
            setError(err.message || "Unable to start checkout.");
            setBusy(false);
        }
    }

    const base = variant === "primary" ? "btn-royal" : "btn-outline-navy";
    return (
        <div className="space-y-2">
            <button type="button" onClick={handleClick} disabled={busy}
                    className={`${base} w-full ${className}`} data-testid={testId}>
                {busy ? "Redirecting to Stripe…" : children}
            </button>
            {error && (
                <p className="text-xs" style={{ color: "#B42318" }} data-testid={`${testId}-error`}>{error}</p>
            )}
        </div>
    );
}

function Hero() {
    return (
        <section className="st-hero relative overflow-hidden" data-testid="landing-hero">
            <CandlestickBackground />
            <div className="st-hero__scrim" aria-hidden="true" />
            <div className="max-w-6xl mx-auto px-6 pt-20 pb-20 md:pt-28 md:pb-24 relative z-10">
                <div className="max-w-3xl animate-fade-in-up">
                    <div className="tracking-caps text-[11px] font-semibold" style={{ color: "var(--st-hero-eyebrow)" }} data-testid="hero-eyebrow">
                        Swing Trading Education Academy
                    </div>
                    <h1 className="font-serif mt-6 text-4xl sm:text-5xl lg:text-6xl leading-[1.05]" style={{ color: "var(--st-hero-heading)" }} data-testid="hero-heading">
                        Trade with the discipline of an institution,
                        <span style={{ color: "var(--st-hero-accent)" }}> not the noise of a chatroom.</span>
                    </h1>
                    <p className="mt-6 font-serif text-lg md:text-xl italic" style={{ color: "var(--st-hero-body)" }} data-testid="hero-subhead">
                        A 12-month structured swing trading education. Nine months of theory. One month of paper trading. Two months of live weekly sessions with the instructor. No shortcuts.
                    </p>
                    <p className="mt-6 text-base md:text-lg leading-relaxed max-w-2xl" style={{ color: "var(--st-hero-body-muted)" }} data-testid="hero-supporting">
                        Trading is like driving. You wouldn't get behind the wheel on day one — and you shouldn't get behind the P&amp;L on day one either. SwingTrust enforces the pace so the skill actually compounds.
                    </p>
                    <div className="mt-10 flex flex-col sm:flex-row gap-4">
                        <a href="#enrollment" className="btn-royal inline-flex items-center justify-center" data-testid="hero-cta-primary">
                            Explore the Curriculum
                        </a>
                        <Link to="/login" className="btn-outline-inverse inline-flex items-center justify-center" data-testid="hero-cta-secondary">
                            Sign In
                        </Link>
                    </div>
                </div>
            </div>
            <div className="st-hero__trust relative z-10" data-testid="hero-trust-strip">
                <div className="max-w-6xl mx-auto px-6 py-4 flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-[11px]">
                    {TRUST_STRIP_ITEMS.map((item, i) => (
                        <span key={item} className="flex items-center">
                            <span className="tracking-caps font-semibold">{item}</span>
                            {i < TRUST_STRIP_ITEMS.length - 1 && <span className="st-divider-dot" />}
                        </span>
                    ))}
                </div>
            </div>
        </section>
    );
}

function DrivingAnalogy() {
    return (
        <section className="py-20 md:py-24" style={{ background: "var(--st-white)" }} data-testid="driving-analogy-section">
            <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-2 gap-12 items-center">
                <div>
                    <div className="tracking-caps text-[11px] font-semibold" style={{ color: "var(--st-royal)" }}>Philosophy</div>
                    <h2 className="font-serif text-3xl md:text-4xl mt-3" style={{ color: "var(--st-navy)" }} data-testid="driving-analogy-heading">
                        Trading is like driving.
                    </h2>
                    <div className="mt-6 space-y-4 text-base md:text-lg leading-relaxed" style={{ color: "var(--st-ink)" }}>
                        <p>Nobody hands you the keys on day one. You study the rules of the road, you practice in controlled conditions, then a licensed instructor sits beside you until you've earned the right to drive alone.</p>
                        <p>Markets deserve the same respect. Nine months of curriculum. One month of paper trading. Two months of live sessions with the instructor. Then, and only then, do you have a real foundation.</p>
                        <p style={{ color: "var(--st-ink-muted)" }}>The pacing is the product. The platform enforces it.</p>
                    </div>
                </div>
                <div className="grid grid-cols-3 gap-4" data-testid="driving-analogy-icons">
                    {[{ icon: BookOpen, label: "Study" }, { icon: Gauge, label: "Simulate" }, { icon: LineChart, label: "Drive" }].map(({ icon: Icon, label }, i) => (
                        <div key={label} className="st-surface p-6 flex flex-col items-center text-center"
                             style={{ background: i === 2 ? "var(--st-silver-50)" : "var(--st-white)" }}>
                            <Icon size={40} strokeWidth={1.4} color="var(--st-navy)" />
                            <div className="mt-4 tracking-caps text-[10px] font-semibold" style={{ color: "var(--st-royal)" }}>Step {i + 1}</div>
                            <div className="font-serif text-lg mt-1" style={{ color: "var(--st-navy)" }}>{label}</div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

function ThreePhase() {
    return (
        <section className="py-20 md:py-24" style={{ background: "var(--st-silver-50)" }} data-testid="curriculum-section">
            <div className="max-w-6xl mx-auto px-6">
                <div className="max-w-2xl">
                    <div className="tracking-caps text-[11px] font-semibold" style={{ color: "var(--st-royal)" }}>Curriculum</div>
                    <h2 className="font-serif text-3xl md:text-4xl mt-3" style={{ color: "var(--st-navy)" }} data-testid="curriculum-heading">
                        The 12-Month Structure
                    </h2>
                    <p className="mt-4 text-base leading-relaxed" style={{ color: "var(--st-ink-muted)" }}>
                        Three phases. Sequenced. Non-skippable. Each unlocks only after the previous is complete.
                    </p>
                </div>
                <div className="mt-12 grid md:grid-cols-3 gap-6" data-testid="curriculum-phases">
                    {PHASES.map((p) => (
                        <div key={p.n} className="st-surface p-8 flex flex-col" data-testid={`phase-card-${p.n}`}>
                            <div className="flex items-start justify-between">
                                <div className="font-serif text-4xl" style={{ color: "var(--st-royal)" }}>{p.n}</div>
                                {p.locked ? (
                                    <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] tracking-caps font-semibold"
                                         style={{ background: "var(--st-silver-100)", color: "var(--st-ink-muted)" }}>
                                        <Lock size={12} strokeWidth={2} /> Locked
                                    </div>
                                ) : (
                                    <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] tracking-caps font-semibold"
                                         style={{ background: "#ECFDF3", color: "#067647" }}>
                                        <CheckCircle2 size={12} strokeWidth={2} /> Unlocked
                                    </div>
                                )}
                            </div>
                            <div className="mt-5">
                                <h3 className="font-serif text-2xl" style={{ color: "var(--st-navy)" }}>{p.name}</h3>
                                <div className="mt-1 tracking-caps text-[11px] font-semibold" style={{ color: "var(--st-silver-500)" }}>{p.duration}</div>
                            </div>
                            <ul className="mt-6 space-y-3 text-sm leading-relaxed flex-1">
                                {p.bullets.map((b) => (
                                    <li key={b} className="flex gap-3" style={{ color: "var(--st-ink)" }}>
                                        <span className="mt-1.5 block w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: "var(--st-royal)" }} />
                                        <span>{b}</span>
                                    </li>
                                ))}
                            </ul>
                            {p.locked && (
                                <div className="mt-6 pt-4 text-xs tracking-caps" style={{ borderTop: "1px solid var(--st-line)", color: "var(--st-silver-500)" }}>
                                    Unlocks after checkpoint
                                </div>
                            )}
                        </div>
                    ))}
                </div>
                <p className="mt-8 text-center text-sm italic" style={{ color: "var(--st-ink-muted)" }} data-testid="curriculum-pacing-note">
                    You cannot skip ahead. The platform enforces the pace.
                </p>
            </div>
        </section>
    );
}

function Instructor({ settings }) {
    const initials = (settings.instructor_initials || "LC").slice(0, 4);
    const bio = settings.instructor_bio_markdown || "";
    const paragraphs = bio.split(/\n\s*\n/).filter(Boolean);
    const portrait = settings.instructor_portrait_url;
    const portraitSrc = portrait
        ? (portrait.startsWith("http") ? portrait : `${process.env.REACT_APP_BACKEND_URL}${portrait}`)
        : null;
    return (
        <section className="py-20 md:py-24" style={{ background: "var(--st-white)" }} data-testid="instructor-section">
            <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-[280px_1fr] gap-12 items-start">
                {portraitSrc ? (
                    <img
                        src={portraitSrc}
                        alt={settings.instructor_name || "Instructor"}
                        className="mx-auto md:mx-0 w-56 h-56 rounded-full object-cover"
                        style={{ boxShadow: "0 10px 40px -20px rgba(10, 31, 68, 0.6)" }}
                        data-testid="instructor-portrait"
                    />
                ) : (
                    <div
                        className="mx-auto md:mx-0 w-56 h-56 rounded-full flex items-center justify-center"
                        style={{
                            background: "radial-gradient(circle at 30% 30%, #12315C 0%, var(--st-navy) 80%)",
                            boxShadow: "0 10px 40px -20px rgba(10, 31, 68, 0.6)",
                        }}
                        data-testid="instructor-portrait"
                    >
                        <span className="font-serif text-6xl" style={{ color: "var(--st-royal)" }}>{initials}</span>
                    </div>
                )}
                <div>
                    <div className="tracking-caps text-[11px] font-semibold" style={{ color: "var(--st-royal)" }}>Meet</div>
                    <h2 className="font-serif text-3xl md:text-4xl mt-3" style={{ color: "var(--st-navy)" }} data-testid="instructor-heading">
                        {settings.instructor_name || "Your Instructor"}
                    </h2>
                    <div className="mt-6 space-y-4 text-base leading-relaxed" style={{ color: "var(--st-ink)" }} data-testid="instructor-bio">
                        {paragraphs.map((para, i) => (
                            <p key={i} style={{ color: i === paragraphs.length - 1 ? "var(--st-ink-muted)" : "var(--st-ink)" }}>{para}</p>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}

function fmtDollars(cents) {
    if (cents === null || cents === undefined) return "";
    const dollars = cents / 100;
    return dollars % 1 === 0 ? `$${dollars.toLocaleString()}` : `$${dollars.toLocaleString(undefined, { minimumFractionDigits: 2 })}`;
}

function Pricing({ plans }) {
    const monthly = plans.find((p) => p.lookup_key === "swingtrust_monthly");
    const yearly = plans.find((p) => p.lookup_key === "swingtrust_yearly");
    if (!monthly || !yearly) return null;

    return (
        <section id="enrollment" className="py-20 md:py-24" style={{ background: "var(--st-silver-50)" }} data-testid="pricing-section">
            <div className="max-w-6xl mx-auto px-6">
                <div className="max-w-2xl">
                    <div className="tracking-caps text-[11px] font-semibold" style={{ color: "var(--st-royal)" }}>Choose your plan</div>
                    <h2 className="font-serif text-3xl md:text-4xl mt-3" style={{ color: "var(--st-navy)" }} data-testid="pricing-heading">Enrollment</h2>
                    <p className="mt-4 text-base leading-relaxed" style={{ color: "var(--st-ink-muted)" }}>
                        Two ways to enroll in the 12-month program. Same curriculum. Same instructor. Same pace.
                    </p>
                </div>

                <div className="mt-12 grid md:grid-cols-2 gap-6 md:items-start">
                    {/* Monthly */}
                    <div className="st-surface p-8 md:mt-8 flex flex-col" data-testid="pricing-monthly-card">
                        <div className="tracking-caps text-[11px] font-semibold" style={{ color: "var(--st-silver-500)" }}>{monthly.display_title}</div>
                        <div className="mt-2 flex items-baseline gap-2">
                            <span className="font-serif text-5xl" style={{ color: "var(--st-navy)" }} data-testid="pricing-monthly-amount">
                                {fmtDollars(monthly.display_amount)}
                            </span>
                            <span className="text-sm" style={{ color: "var(--st-ink-muted)" }}>/ {monthly.display_interval}</span>
                        </div>
                        <p className="mt-4 text-sm leading-relaxed" style={{ color: "var(--st-ink-muted)" }}>{monthly.subtitle}</p>
                        <ul className="mt-6 space-y-3 text-sm flex-1">
                            {(monthly.features || []).map((b) => (
                                <li key={b} className="flex gap-3" style={{ color: "var(--st-ink)" }}>
                                    <CheckCircle2 size={16} strokeWidth={2} color="var(--st-royal)" className="mt-0.5 flex-shrink-0" />
                                    <span>{b}</span>
                                </li>
                            ))}
                        </ul>
                        <div className="mt-8">
                            <CheckoutButtons plan="monthly" variant="secondary" testId="pricing-monthly-cta">Start Monthly</CheckoutButtons>
                        </div>
                    </div>

                    {/* Yearly */}
                    <div className="relative p-8 rounded-lg flex flex-col"
                         style={{ background: "var(--st-white)", border: "2px solid var(--st-royal)", boxShadow: "0 24px 60px -30px rgba(30, 72, 214, 0.4)" }}
                         data-testid="pricing-yearly-card">
                        <div className="absolute -top-3 left-1/2 -translate-x-1/2 tracking-caps text-[10px] font-bold px-3 py-1.5 rounded-full"
                             style={{ background: "var(--st-navy)", color: "var(--st-white)" }} data-testid="pricing-yearly-ribbon">
                            BEST VALUE — RECOMMENDED
                        </div>
                        <div className="flex items-center justify-between">
                            <div className="tracking-caps text-[11px] font-semibold" style={{ color: "var(--st-royal)" }}>{yearly.display_title}</div>
                            {yearly.savings_badge && (
                                <span className="px-2.5 py-1 rounded-full text-[10px] tracking-caps font-bold"
                                      style={{ background: "var(--st-royal)", color: "var(--st-white)" }}
                                      data-testid="pricing-yearly-savings-badge">
                                    {yearly.savings_badge}
                                </span>
                            )}
                        </div>
                        <div className="mt-2 flex items-baseline gap-2">
                            <span className="font-serif text-5xl" style={{ color: "var(--st-navy)" }} data-testid="pricing-yearly-amount">
                                {fmtDollars(yearly.display_amount)}
                            </span>
                            <span className="text-sm" style={{ color: "var(--st-ink-muted)" }}>/ {yearly.display_interval}</span>
                        </div>
                        <div className="mt-1 text-xs" style={{ color: "var(--st-silver-500)" }}>
                            <span className="line-through">{fmtDollars((monthly.display_amount || 0) * 12)} if paid monthly</span>
                        </div>
                        <p className="mt-4 text-sm leading-relaxed" style={{ color: "var(--st-ink-muted)" }}>{yearly.subtitle}</p>
                        <ul className="mt-6 space-y-3 text-sm flex-1">
                            {(yearly.features || []).map((b) => (
                                <li key={b} className="flex gap-3" style={{ color: "var(--st-ink)" }}>
                                    <CheckCircle2 size={16} strokeWidth={2} color="var(--st-royal)" className="mt-0.5 flex-shrink-0" />
                                    <span>{b}</span>
                                </li>
                            ))}
                        </ul>
                        <div className="mt-8">
                            <CheckoutButtons plan="yearly" variant="primary" testId="pricing-yearly-cta">Enroll Yearly</CheckoutButtons>
                        </div>
                    </div>
                </div>

                <div className="mt-10 p-6 rounded-lg flex gap-4 items-start"
                     style={{ background: "var(--st-white)", border: "1px solid var(--st-navy)" }}
                     data-testid="pricing-no-refunds-notice">
                    <Shield size={22} strokeWidth={1.6} color="var(--st-navy)" className="flex-shrink-0 mt-0.5" />
                    <p className="text-sm md:text-base leading-relaxed" style={{ color: "var(--st-ink)" }}>
                        <strong style={{ color: "var(--st-navy)" }}>All enrollments are final. No refunds on either plan.</strong>{" "}
                        The monthly plan serves as the trial — one month at {fmtDollars(monthly.display_amount)} lets you experience the program before committing yearly. Choose deliberately.
                    </p>
                </div>
            </div>
        </section>
    );
}

function FAQ({ items }) {
    if (!items || items.length === 0) return null;
    return (
        <section className="py-20 md:py-24" style={{ background: "var(--st-white)" }} data-testid="faq-section">
            <div className="max-w-4xl mx-auto px-6">
                <div className="text-center max-w-2xl mx-auto">
                    <div className="tracking-caps text-[11px] font-semibold" style={{ color: "var(--st-royal)" }}>Answers</div>
                    <h2 className="font-serif text-3xl md:text-4xl mt-3" style={{ color: "var(--st-navy)" }} data-testid="faq-heading">
                        Frequently Asked Questions
                    </h2>
                </div>
                <div className="mt-12">
                    <Accordion type="single" collapsible className="space-y-3" data-testid="faq-accordion">
                        {items.map((item, i) => (
                            <AccordionItem key={i} value={`item-${i}`} className="st-surface px-6" data-testid={`faq-item-${i}`}>
                                <AccordionTrigger className="text-left font-serif text-lg hover:no-underline" style={{ color: "var(--st-navy)" }}>
                                    {item.q}
                                </AccordionTrigger>
                                <AccordionContent className="text-base leading-relaxed pb-6" style={{ color: "var(--st-ink)" }}>
                                    {item.a}
                                </AccordionContent>
                            </AccordionItem>
                        ))}
                    </Accordion>
                </div>
            </div>
        </section>
    );
}

function FinalBand() {
    return (
        <section className="py-20 md:py-24" style={{ background: "var(--st-navy)" }} data-testid="final-cta-band">
            <div className="max-w-4xl mx-auto px-6 text-center">
                <h2 className="font-serif text-4xl md:text-5xl" style={{ color: "var(--st-white)" }} data-testid="final-cta-heading">
                    Commit to the process.
                </h2>
                <p className="mt-6 text-lg leading-relaxed max-w-2xl mx-auto" style={{ color: "#C4CBDA" }}>
                    Twelve months. No shortcuts. A structured path to becoming a self-directed swing trader.
                </p>
                <div className="mt-10">
                    <a href="#enrollment" className="btn-royal inline-flex items-center justify-center" data-testid="final-cta-btn">
                        See the Curriculum
                    </a>
                </div>
            </div>
        </section>
    );
}

export default function Landing() {
    const [plans, setPlans] = useState(null);
    const [settings, setSettings] = useState(null);

    useEffect(() => {
        api.publicPricing().then((r) => setPlans(r.plans)).catch(() => setPlans([]));
        api.publicSiteSettings().then(setSettings).catch(() => setSettings({}));
    }, []);

    return (
        <PageShell>
            <Hero />
            <DrivingAnalogy />
            <ThreePhase />
            {/* Instructor section intentionally removed from public landing (2026-02).
                CMS + admin editor + DB + API remain intact — see AdminSettings.jsx
                and /api/site-settings — flip back on by re-rendering <Instructor> here. */}
            {plans && <Pricing plans={plans} />}
            <Testimonials />
            {settings && <FAQ items={settings.faq || []} />}
            <FinalBand />
        </PageShell>
    );
}
